import React from 'react'
import _ from 'lodash'
import { makeStyles } from '@material-ui/core/styles'
import { Button, Typography } from '@material-ui/core'
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf'
import DescriptionIcon from '@material-ui/icons/Description'
import { useTranslation } from 'react-i18next'

import Modal from '../modal'
import StreamDescriptionFromPdf from './StreamDescriptionFromPdf'
import useSubmittedProposals, {
  getDescriptionPdfUrl
} from '../hooks/useSubmittedProposals'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column'
  },
  actionButton: {
    width: '100%',
    justifyContent: 'flex-start',
    padding: '0.75rem 1rem',
    textAlign: 'left',
    textTransform: 'none',
    marginBottom: '0.75rem'
  },
  sectionTitle: {
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: '0.75rem'
  },
  actionButtonContent: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%'
  },
  actionIcon: {
    marginRight: '0.75rem',
    marginTop: '0.125rem'
  },
  actionLabel: {
    fontWeight: 700,
    fontSize: '0.875rem',
    lineHeight: 1.3
  },
  actionHint: {
    display: 'block',
    fontSize: '0.75rem',
    fontWeight: 400,
    opacity: 0.8,
    marginTop: '0.125rem'
  }
}))

export function hasTextDescription(proposal) {
  if (!proposal?.descriptions?.length) {
    return false
  }

  const longTextObj = proposal.descriptions.find(element =>
    Boolean(element.long)
  )

  if (_.isEmpty(longTextObj) || !longTextObj.long) {
    return false
  }

  const strippedText = longTextObj.long.replace(/<[^>]*>/g, '').trim()
  return strippedText.length > 0
}

export function getDescriptionLong(proposal) {
  const longTextObj =
    proposal.descriptions.length > 0
      ? proposal.descriptions.find(element => Boolean(element.long))
      : {}
  const html = !_.isEmpty(longTextObj)
    ? longTextObj.long
    : '<p>No description</p>'

  return html
}

function InfoTabDescriptions({ proposal, proposalId }) {
  const classes = useStyles()
  const { t } = useTranslation('proposals')
  const { descriptionPdfUrls } = useSubmittedProposals()
  const [textModalOpen, setTextModalOpen] = React.useState(false)
  const [pdfModalOpen, setPdfModalOpen] = React.useState(false)

  const hasText = hasTextDescription(proposal)
  const descriptionPdfUrl = getDescriptionPdfUrl(
    descriptionPdfUrls,
    proposal,
    proposalId
  )
  const hasPdf = Boolean(descriptionPdfUrl)

  return (
    <div className={classes.root}>
      <Typography className={classes.sectionTitle}>
        {t('Descriptions')}
      </Typography>

      <Button
        className={classes.actionButton}
        size="medium"
        variant="contained"
        color="primary"
        disabled={!hasPdf}
        onClick={() => setPdfModalOpen(true)}
        startIcon={null}
      >
        <span className={classes.actionButtonContent}>
          <PictureAsPdfIcon className={classes.actionIcon} fontSize="small" />
          <span>
            <span className={classes.actionLabel}>
              {t('View PDF description')}
            </span>
            <span className={classes.actionHint}>
              {hasPdf
                ? t('Required PDF submission')
                : t('PDF description not available')}
            </span>
          </span>
        </span>
      </Button>

      <Button
        className={classes.actionButton}
        size="medium"
        variant="outlined"
        color="primary"
        disabled={!hasText}
        onClick={() => setTextModalOpen(true)}
      >
        <span className={classes.actionButtonContent}>
          <DescriptionIcon className={classes.actionIcon} fontSize="small" />
          <span>
            <span className={classes.actionLabel}>
              {t('View text description')}
            </span>
            <span className={classes.actionHint}>
              {hasText
                ? t('Optional text submission')
                : t('Text description not available')}
            </span>
          </span>
        </span>
      </Button>

      <Modal
        close={() => setTextModalOpen(false)}
        isOpen={textModalOpen}
        title={t('Description text')}
        proposal={proposal}
      >
        <div
          dangerouslySetInnerHTML={{
            __html: getDescriptionLong(proposal)
          }}
        />
      </Modal>

      <Modal
        close={() => setPdfModalOpen(false)}
        isOpen={pdfModalOpen}
        title={t('Description PDF')}
        proposal={proposal}
      >
        <StreamDescriptionFromPdf
          proposal={proposal}
          proposalId={proposalId}
          fileUrl={descriptionPdfUrl}
        />
      </Modal>
    </div>
  )
}

export default InfoTabDescriptions
